import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../../types/user';
import { loadAllActiveEmployees, SimpleUser } from '../../../services/userLoader';
import { LeaveService } from '../../../services/leave';
import { LeaveBalanceService } from '../../../services/leaveBalanceService';
import { LeaveRequest, LeaveType, LeaveStatus } from '../../../types/leave';
import { LeavePageHeader, YearSelect } from './leaveShared';
import { sameIdentity } from '../../../services/identity';
import EmployeeLeaveProfileDrawer from '../EmployeeLeaveProfileDrawer';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

interface MatrixRow {
  employee: SimpleUser;
  leaves: LeaveRequest[];
  remainingBalance: number | null;
}

const TeamLeaveMatrixPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [rows, setRows] = useState<MatrixRow[]>([]);
  /** First paint and year changes — there is nothing on screen, so it says so. */
  const [loading, setLoading] = useState<boolean>(true);
  /**
   * Event-driven reloads (an approval elsewhere, a storage event). The table on screen is still
   * valid and only about to be replaced, so it dims rather than being replaced by a loading line
   * — the whole matrix vanishing every time somebody approved a leave was worse than a stale
   * figure for half a second.
   */
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  /** Which employee's full leave history is open. One at a time keeps the table readable. */
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | LeaveStatus>('ALL');
  const [typeFilter, setTypeFilter] = useState<'ALL' | LeaveType>('ALL');
  const [monthFilter, setMonthFilter] = useState<'ALL' | number>('ALL');

  /**
   * Whether this viewer sees a team or the whole company.
   *
   * Drives the wording of the count line and the empty state only — the scope itself is decided
   * in loadMatrix. Worth naming because "no employees" means two entirely different things to a
   * manager with no reports and to an HR Admin looking at an unreadable directory.
   */
  const isTeamScope = user?.role === UserRole.MANAGER;

  // Employee Leave Profile Drawer State
  const [selectedEmployee, setSelectedEmployee] = useState<SimpleUser | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [allLeavesList, setAllLeavesList] = useState<LeaveRequest[]>([]);

  /**
   * The whole matrix for everyone in the viewer's scope — a Manager's team, or every employee for
   * HR and Super Admin.
   *
   * This used to build rows only for employees matching a search term, and rendered nothing at all
   * until one was typed. That made the page unusable for what it is for: a manager cannot plan
   * cover for a team whose names they have to already know and enter one at a time, and "who is
   * off in March" was unanswerable. Search is now a filter over rows that are already loaded (see
   * filteredRows), not the thing that decides whether any exist.
   *
   * Scope comes from loadAllActiveEmployees, which takes the manager's own address for the team
   * case — the same domain-aware reporting-line test the rest of the app uses, kept in the shared
   * helper rather than repeated here. It also filters out disabled accounts, which is a change for
   * the Manager case: the old loadDirectReports did not, so a manager's matrix listed people who
   * had left while an admin's did not.
   *
   * Balances come from getBalancesForAll, not from getBalances per employee. That is what makes
   * loading everyone affordable — it is a fixed number of round trips for the whole run, where the
   * old per-employee loop issued five fetches each and would have meant hundreds of requests for
   * an HR Admin. It is also the same code path the Employee Profile drawer and Leave Reports use,
   * so the three cannot disagree about the same person's balance.
   */
  const loadMatrix = useCallback(async (background = false) => {
    if (background) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const scoped = user?.role === UserRole.MANAGER
        ? await loadAllActiveEmployees(user.email)
        // HR_Admin and Super_Admin: the whole company. The route allows no other role
        // (App.tsx gates it to Manager/HR_Admin/Super_Admin), so there is no third case.
        : await loadAllActiveEmployees();

      const [allLeaves, balances] = await Promise.all([
        LeaveService.listLeaves(),
        LeaveBalanceService.getBalancesForAll(selectedYear),
      ]);
      setAllLeavesList(allLeaves);

      // Keyed by canonical-ish lower-cased email for the common case, with a domain-aware
      // fallback below: getBalancesForAll returns a row per directory entry, and the scoped list
      // is drawn from the same directory, so this is a lookup rather than a second computation.
      const balanceByEmail = new Map(
        balances.map(({ user: u, snapshots }) => [
          (u.email || '').trim().toLowerCase(),
          snapshots.reduce((sum, s) => sum + s.balance, 0),
        ])
      );

      const matrixRows: MatrixRow[] = scoped
        .map((emp) => {
          const empLeaves = allLeaves
            .filter((l) => sameIdentity(l.employeeEmail, emp.email))
            .filter((l) => new Date(l.startDate).getFullYear() === selectedYear)
            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

          const key = (emp.email || '').trim().toLowerCase();
          const exact = balanceByEmail.get(key);
          // A person holds two addresses on two domains, so an exact key miss is not "no
          // balance" — it is the other domain's spelling. Falls back to the domain-aware
          // comparison rather than printing '—' for anyone signed up under the older domain.
          const remainingBalance = exact !== undefined
            ? exact
            : balances.find(({ user: u }) => sameIdentity(u.email, emp.email))
                ?.snapshots.reduce((sum, s) => sum + s.balance, 0) ?? null;

          return { employee: emp, leaves: empLeaves, remainingBalance };
        })
        // Cognito's listUsers has no meaningful order, so without this the row order shifted
        // between loads and there was no way to find a person except by searching — the very
        // thing this page no longer requires.
        .sort((a, b) =>
          (a.employee.fullName || a.employee.name || a.employee.email)
            .localeCompare(b.employee.fullName || b.employee.name || b.employee.email)
        );

      setRows(matrixRows);
    } catch {
      setError('Unable to load team leave matrix.');
      setRows([]);
    } finally {
      // finally, not after the awaits: a failed fetch must still clear the loading line, or the
      // page reads as "still loading" when it has in fact given up.
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.role, user?.email, selectedYear]);

  // Without this, the "Remaining Balance" column and the Employee Profile drawer both
  // kept showing whatever they had when the matrix last loaded — this is the root cause of
  // the "Employee Profile doesn't update after Grant Leave" sync bug.
  useEffect(() => {
    loadMatrix();
    // A separate handler so the listeners take the background path. Registering loadMatrix
    // itself would pass the DOM Event as its `background` argument, which is truthy — so every
    // foreground reload would silently have been treated as a background one.
    const onExternalChange = () => { loadMatrix(true); };
    window.addEventListener('leave-balance-updated', onExternalChange);
    window.addEventListener('leave-requests-updated', onExternalChange);
    window.addEventListener('storage', onExternalChange);
    return () => {
      window.removeEventListener('leave-balance-updated', onExternalChange);
      window.removeEventListener('leave-requests-updated', onExternalChange);
      window.removeEventListener('storage', onExternalChange);
    };
  }, [loadMatrix]);

  const leaveFiltersActive = statusFilter !== 'ALL' || typeFilter !== 'ALL' || monthFilter !== 'ALL';

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      // Search narrows what is already loaded rather than deciding what to load, so it needs no
      // debounce and no round trip — typing filters the table on the keystroke.
      .filter((r) => {
        if (!term) return true;
        const name = (r.employee.fullName || r.employee.name || '').toLowerCase();
        return name.includes(term) || r.employee.email.toLowerCase().includes(term);
      })
      .map((r) => {
        const matching = r.leaves.filter((l) => {
          if (statusFilter !== 'ALL' && l.status !== statusFilter) return false;
          if (typeFilter !== 'ALL' && l.type !== typeFilter) return false;
          if (monthFilter !== 'ALL' && new Date(l.startDate).getMonth() !== monthFilter) return false;
          return true;
        });
        // matchingLeaves carries the whole set so the expanded row can show every record;
        // displayLeave stays as the most recent one for the collapsed summary line.
        return { ...r, matchingLeaves: matching, displayLeave: matching[0] || null, hasMatch: matching.length > 0 };
      })
      .filter((r) => (leaveFiltersActive ? r.hasMatch : true));
  }, [rows, search, statusFilter, typeFilter, monthFilter, leaveFiltersActive]);

  return (
    <section className="leave-page">
      <LeavePageHeader
        title="Team Leave Matrix"
        // forward={1}: current year + next only — the matrix is a near-term planning view.
        actions={<YearSelect value={selectedYear} onChange={setSelectedYear} forward={1} />}
      />

      <div className="lb-summary-header">
        {/* Filter toolbar */}
        <div className="lb-matrix-filters">
          {/* No longer autoFocus: the table now loads on its own, so stealing focus on arrival
              scrolls a long matrix out of view before it has been read. */}
          <input
            type="text"
            className="lb-matrix-search"
            placeholder="🔍 Filter by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'ALL' | LeaveStatus)}>
            <option value="ALL">All Statuses</option>
            {Object.values(LeaveStatus).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as 'ALL' | LeaveType)}>
            <option value="ALL">All Leave Types</option>
            {Object.values(LeaveType).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={monthFilter === 'ALL' ? 'ALL' : String(monthFilter)}
            onChange={(e) => setMonthFilter(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
          >
            <option value="ALL">All Months</option>
            {MONTH_NAMES.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
        </div>

        {/* Row count, so a filtered view is never mistaken for the whole team. Hidden while there
            is nothing to count. */}
        {!loading && !error && rows.length > 0 && (
          <p className="lb-ineligible-desc" style={{ margin: '0 0 10px' }}>
            Showing {filteredRows.length} of {rows.length}{' '}
            {rows.length === 1 ? 'employee' : 'employees'} for {selectedYear}
            {isTeamScope ? ' · your direct reports' : ''}
          </p>
        )}

        {loading ? (
          <p className="lb-ineligible-desc">Loading leave matrix...</p>
        ) : error ? (
          <p className="lb-ineligible-desc">{error}</p>
        ) : rows.length === 0 ? (
          <p className="lb-ineligible-desc">
            {isTeamScope
              // Distinguished because they are different problems with different fixes: an empty
              // team is a reporting-line gap in User Management, not a failure of this page.
              ? 'No employees report to you yet. Reporting lines are set in Settings › User Management.'
              : 'No active employees found in the directory.'}
          </p>
        ) : filteredRows.length === 0 ? (
          <p className="lb-ineligible-desc">
            {search.trim()
              ? `No employees match "${search}"${leaveFiltersActive ? ' with the selected leave filters' : ''}.`
              : 'No leave records match the selected filters.'}
          </p>
        ) : (
          <table className={`lb-team-matrix-table${refreshing ? ' lr-refreshing' : ''}`}>
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Latest Leave</th>
                <th>Leave Dates</th>
                <th>Status</th>
                <th>Remaining Balance</th>
                <th>Records</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const isExpanded = expandedEmail === r.employee.email;
                const count = r.matchingLeaves.length;
                return (
                  <React.Fragment key={r.employee.email}>
                    <tr
                      key={r.employee.email}
                      tabIndex={0}
                      role="button"
                      aria-label={`View leave profile for ${r.employee.fullName || r.employee.name}`}
                      className={`lb-matrix-row is-clickable ${isExpanded ? 'is-expanded' : ''}`}
                      onClick={() => {
                        setSelectedEmployee(r.employee);
                        setDrawerOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedEmployee(r.employee);
                          setDrawerOpen(true);
                        }
                      }}
                    >
                      <td>
                        <div className="lb-emp-cell">
                          <strong className="lb-emp-name-link">{r.employee.fullName || r.employee.name}</strong>
                          <span>{r.employee.email}</span>
                        </div>
                      </td>
                      {count === 0 ? (
                        <td colSpan={3} className="lb-matrix-noleave">
                          No leave taken in {selectedYear}
                        </td>
                      ) : (
                        <>
                          <td>{r.displayLeave?.type}</td>
                          <td>{r.displayLeave ? `${r.displayLeave.startDate} to ${r.displayLeave.endDate}` : '—'}</td>
                          <td>
                            {r.displayLeave && (
                              <span className={`lb-status-badge status-${String(r.displayLeave.status).toLowerCase()}`}>
                                {r.displayLeave.status}
                              </span>
                            )}
                          </td>
                        </>
                      )}
                      <td>{r.remainingBalance !== null ? <span className="lb-matrix-val">{r.remainingBalance}</span> : '—'}</td>
                      <td>
                        {count > 0 ? (
                          <button
                            type="button"
                            className="lb-matrix-expand"
                            aria-expanded={isExpanded}
                            onClick={(e) => { e.stopPropagation(); setExpandedEmail(isExpanded ? null : r.employee.email); }}
                          >
                            {isExpanded ? '▼' : '▶'} {count}
                          </button>
                        ) : <span className="lb-matrix-noleave">0</span>}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="lb-matrix-detail-row">
                        <td colSpan={6}>
                          <table className="lb-matrix-inner-table">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>From</th>
                                <th>To</th>
                                <th>Session</th>
                                <th>Status</th>
                                <th>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.matchingLeaves.map((l) => (
                                <tr key={l.id}>
                                  <td>{l.type}</td>
                                  <td>{l.startDate}</td>
                                  <td>{l.endDate}</td>
                                  <td>{l.session || 'FULL'}</td>
                                  <td>
                                    <span className={`lb-status-badge status-${String(l.status).toLowerCase()}`}>
                                      {l.status}
                                    </span>
                                  </td>
                                  <td>{l.reason || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Employee Leave Profile Drawer */}
      <EmployeeLeaveProfileDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        employee={selectedEmployee}
        leaves={allLeavesList}
        year={selectedYear}
      />
    </section>
  );
};

export default TeamLeaveMatrixPage;
